import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { useEffect, useMemo } from 'react'
import { useProcessSuggestionCards } from './useProcessSuggestionCards'
import { useTrackChangeCards } from './useTrackChangeCards'

/**
 * Effect hook to process suggestion cards on the tree.
 * Runs when default suggestion card filter is active and loading state is not blocking updates.
 * @param {boolean} showLoadingState - Indicates whether the loading state is shown.
 * @param {string[]} articlesLoading - Array of article IDs that are currently loading.
 * @return {void}
 * */
export const useProcessSuggestionCardsOnTreeEffect = (
    showLoadingState: boolean,
    articlesLoading: string[]
) => {
    const { isNotDefaultSuggestionCardFilter } = useTrackChangeCards()
    const { processSuggestionCards } = useProcessSuggestionCards()
    const { memoContentItemViewNodes } = useShallowSelector((s) => ({
        memoContentItemViewNodes: s.contentItemViewNodes,
    }))

    const articlesLoadingState = useMemo(
        () => ({
            length: articlesLoading.length,
            hasDefault: articlesLoading.includes('default'),
            isEmpty: articlesLoading.length === 0,
            isOnlyDefault:
                articlesLoading.length === 1 &&
                articlesLoading[0] === 'default',
        }),
        [articlesLoading]
    )

    useEffect(() => {
        const isDefaultFilterActive = !isNotDefaultSuggestionCardFilter
        const shouldProcess =
            isDefaultFilterActive &&
            (!showLoadingState ||
                articlesLoadingState.isOnlyDefault ||
                articlesLoadingState.isEmpty)

        if (shouldProcess) {
            processSuggestionCards(memoContentItemViewNodes)
        }
    }, [
        showLoadingState,
        articlesLoadingState,
        memoContentItemViewNodes,
        isNotDefaultSuggestionCardFilter,
        processSuggestionCards,
    ])
}
